import type { MoveToSetType } from "../../../../api/pipelineBatchUpdates.service.js";
import {
  SKIP_REASON_LABELS,
  type InvalidMoveToSetRow,
  type MoveToSetCsvRow,
  type MoveToSetSkipReason,
  type SkippedMoveToSetRow,
} from "./parseMoveToSetCsv.js";
import type { MoveToSetFamilyLink } from "./useMoveToSetBatch.js";

export type FamilyConflictSeverity = "blocking" | "warning";

export type FamilyConflict = {
  mateId: string;
  requestedId: string;
  severity: FamilyConflictSeverity;
  reason: string;
};

export type FamilyConflictFile = {
  rows: MoveToSetCsvRow[];
  skipped: SkippedMoveToSetRow[];
  invalid: InvalidMoveToSetRow[];
};

type FileEntry =
  | { kind: "row"; testimonialType: MoveToSetType }
  | { kind: "skipped"; reason: MoveToSetSkipReason }
  | { kind: "invalid"; line: number; reason: string };

function indexFile({ rows, skipped, invalid }: FamilyConflictFile): Map<string, FileEntry> {
  const entries = new Map<string, FileEntry>();
  for (const row of invalid) {
    const id = row.testimonialId.toLowerCase();
    if (id) entries.set(id, { kind: "invalid", line: row.line, reason: row.reason });
  }
  for (const row of skipped)
    entries.set(row.testimonialId, { kind: "skipped", reason: row.reason });
  for (const row of rows) {
    entries.set(row.testimonialId, { kind: "row", testimonialType: row.testimonialType });
  }
  return entries;
}

function mateConflict(
  link: MoveToSetFamilyLink,
  entry: FileEntry | undefined,
): FamilyConflict | null {
  const { mateId, requestedId, testimonialType } = link;
  const sent = `but its family is sent as ${testimonialType}`;
  if (!entry) {
    if (link.outcome !== "moved") return null;
    return {
      mateId,
      requestedId,
      severity: "warning",
      reason: `not in the file, moves to ${testimonialType} with its family`,
    };
  }
  if (entry.kind === "row") {
    if (entry.testimonialType === testimonialType) return null;
    return {
      mateId,
      requestedId,
      severity: "blocking",
      reason: `in the file as ${entry.testimonialType}, ${sent}`,
    };
  }
  const why =
    entry.kind === "skipped"
      ? `skipped in the file (${SKIP_REASON_LABELS[entry.reason]})`
      : `invalid in the file (line ${entry.line}: ${entry.reason})`;
  if (link.outcome === "unchanged") {
    return {
      mateId,
      requestedId,
      severity: "warning",
      reason: `${why}, already ${testimonialType} with no barrister link, so the move leaves it unchanged`,
    };
  }
  return { mateId, requestedId, severity: "blocking", reason: `${why}, ${sent}` };
}

function familiesOf(links: MoveToSetFamilyLink[]): string[][] {
  const parent = new Map<string, string>();
  const root = (id: string): string => {
    let current = id;
    while (parent.get(current) !== current) current = parent.get(current) ?? current;
    parent.set(id, current);
    return current;
  };
  const add = (id: string) => {
    if (!parent.has(id)) parent.set(id, id);
  };
  for (const { mateId, requestedId } of links) {
    add(requestedId);
    add(mateId);
    const [a, b] = [root(requestedId), root(mateId)];
    if (a !== b) parent.set(b, a);
  }
  const families = new Map<string, string[]>();
  for (const id of parent.keys()) {
    const key = root(id);
    families.set(key, [...(families.get(key) ?? []), id]);
  }
  return [...families.values()];
}

function mixedTypeConflicts(
  links: MoveToSetFamilyLink[],
  entries: Map<string, FileEntry>,
): FamilyConflict[] {
  return familiesOf(links).flatMap(members => {
    const byType: Record<MoveToSetType, string[]> = { set_testimonial: [], clerk_testimonial: [] };
    for (const id of members) {
      const entry = entries.get(id);
      if (entry?.kind === "row") byType[entry.testimonialType].push(id);
    }
    const [setIds, clerkIds] = [byType.set_testimonial, byType.clerk_testimonial];
    if (setIds.length === 0 || clerkIds.length === 0) return [];
    return [
      {
        mateId: clerkIds[0],
        requestedId: setIds[0],
        severity: "blocking" as const,
        reason: `one family has rows of both types in the file: set_testimonial ${setIds.join(" ")}; clerk_testimonial ${clerkIds.join(" ")}`,
      },
    ];
  });
}

export function findFamilyConflicts(
  links: MoveToSetFamilyLink[],
  file: FamilyConflictFile,
): FamilyConflict[] {
  const entries = indexFile(file);
  const byMate = new Map<string, FamilyConflict>();
  for (const link of links) {
    const reported = byMate.get(link.mateId);
    if (reported?.severity === "blocking") continue;
    const conflict = mateConflict(link, entries.get(link.mateId));
    if (!conflict || (reported && conflict.severity !== "blocking")) continue;
    byMate.set(link.mateId, conflict);
  }
  return [...byMate.values(), ...mixedTypeConflicts(links, entries)];
}

export function countBlocking(conflicts: FamilyConflict[]): number {
  return conflicts.filter(conflict => conflict.severity === "blocking").length;
}
